import { Box, Divider, FlexBox } from '@dixa/lib-shared-habla';
import { SearchResult } from 'lib-search-types';
import { MetaConversation } from 'lib-shared-legacy-types';
import type { Conversation, Tag } from 'lib-shared-types-api';

import { ConversationStatusColorBox } from 'components/conversation';
import { ConversationPreviewCustomFields } from 'components/conversation/conversationPreview/components/customFields/conversationPreviewCustomFields';
import { ConversationPreviewTags } from 'components/conversation/conversationPreview/components/tags/conversationPreviewTags';

import { ConversationLegacyPreviewActions } from './components/actions/conversationLegacyPreviewActions';
import { ConversationLegacyPreviewCustomer } from './components/customer/conversationLegacyPreviewCustomer';
import { ConversationLegacyPreviewHeadline } from './components/headline/conversationLegacyPreviewHeadline';
import styles from './conversationLegacyPreviewHeader.module.scss';

type ConversationLegacyPreviewHeaderProps = {
  csid: number;
  metaConversation: MetaConversation;
  highlights: SearchResult<'conversations'>['highlights'];
  onPreviewClose: () => void;
  onGoToConversation: () => void;
  /** Whether to show the go-to/close actions. Defaults to true. */
  showActions?: boolean;
  /** Whether to show tags and custom fields. Defaults to true. */
  showDetails?: boolean;
  /** Whether to show the customer/end-user contact details. Defaults to false. */
  showCustomer?: boolean;
};

export const ConversationLegacyPreviewHeader = ({
  csid,
  metaConversation,
  highlights,
  onPreviewClose,
  onGoToConversation,
  showActions = true,
  showDetails = true,
  showCustomer = false,
}: ConversationLegacyPreviewHeaderProps): JSX.Element => {
  const conversation = metaConversation.conversation as Conversation;

  return (
    <>
      <FlexBox flexDirection="row">
        <ConversationStatusColorBox
          csid={conversation.id}
          status={conversation.status}
        />

        <Box className={styles.content} flex="1" padding="300 400">
          <ConversationLegacyPreviewHeadline
            metaConversation={metaConversation}
            highlights={highlights}
          />

          {showActions && (
            <ConversationLegacyPreviewActions
              onGoToConversation={onGoToConversation}
              onClose={onPreviewClose}
              metaConversation={metaConversation}
              csid={csid}
            />
          )}

          {showCustomer && (
            <ConversationLegacyPreviewCustomer
              requester={metaConversation.requester}
              showSectionIcon
            />
          )}

          {showDetails && (
            <>
              <ConversationPreviewTags
                tags={
                  (metaConversation.conversation.tags ?? []) as readonly Tag[]
                }
                showSectionIcon
              />

              {typeof metaConversation.conversation.id === 'number' && (
                <ConversationPreviewCustomFields
                  csid={metaConversation.conversation.id}
                  showSectionIcon
                />
              )}
            </>
          )}
        </Box>
      </FlexBox>

      <Divider />
    </>
  );
};
